/**
 * O controlador da apresentação por passos — quem esconde, quem revela e quem restaura.
 *
 * ═══ Onde ele é chamado, e por que MUDOU ═══════════════════════════════════════════════════
 *
 * A primeira versão ouvia `keydown` em fase de **captura** para ganhar da barra de
 * apresentação. A corrida é real e está medida no dossiê — mas o ouvinte só via **teclas**, e
 * a seta da barra é um **clique** que chama `_nextFrame()` direto.
 *
 * Resultado em campo: pelo teclado os elementos apareciam; pelo botão a apresentação pulava
 * para o próximo quadro **deixando-os escondidos**. O operador viu, e a frase dele é o
 * critério: *"ambas precisam seguir a exibição dos elementos"*.
 *
 * Agora quem chama é o próprio `_nextFrame`/`_previousFrame` (patch declarado em
 * `infra/affine/patches/0009`), e os dois caminhos ficam iguais **por construção** — não por
 * coincidência de dois códigos que alguém lembrou de manter iguais. A corrida sumiu junto: não
 * disputamos mais a tecla, somos chamados por quem a recebeu.
 *
 * ═══ Por que `display` e não `hidden` ══════════════════════════════════════════════════════
 *
 * `display` é `@local` (`element-model.ts:345`) — vive na sessão, não vai para o Y.Doc.
 * `hidden` é `@field` (`:360`) — sincronizado.
 *
 * Apresentar com `hidden` alteraria o documento **de todos**, e uma apresentação interrompida
 * (F5, aba fechada, bateria) deixaria elementos invisíveis **para sempre, para todo mundo**.
 * Com `display`, o pior caso é local e morre ao recarregar. Ainda assim se restaura ao sair.
 *
 * ═══ O grupo ═══════════════════════════════════════════════════════════════════════════════
 *
 * O renderer percorre uma lista PLANA (`canvas-renderer.ts:1161`) e lê `display` **por
 * elemento** (`:1168`); o renderer do grupo desenha só o título e a borda. Ocultar o grupo
 * esconderia a decoração e deixaria o conteúdo na tela. Por isso `alcance()` desce a árvore.
 */

import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import type { FrameBlockModel } from '@blocksuite/affine-model';
import { DisposableGroup } from '@blocksuite/global/disposable';
import {
  type GfxController,
  GfxExtension,
  GfxExtensionIdentifier,
  type GfxModel,
  isGfxGroupCompatibleModel,
} from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';

import { EdgelessFrameManagerIdentifier } from '../frame-manager';
import { PresentTool } from '../present-tool';
import { passosDoFrame } from './marcacao';
import { type HostDeBloco, ocultarBloco } from './oculta-bloco';
import {
  ocultosApos,
  type PassoResolvido,
  podeDesrevelar,
  podeRevelar,
  ocultosDosFuturos,
  totalDePassos,
} from './passos';

export const CHAVE_DO_CONTROLADOR = 'tuninho-passos-de-apresentacao';

export class ControladorDePassos extends GfxExtension {
  static override key = CHAVE_DO_CONTROLADOR;

  private readonly _disposables = new DisposableGroup();

  /** O frame que está sendo apresentado agora. `null` fora da apresentação. */
  private _frame: FrameBlockModel | null = null;

  /** Os passos do frame atual, ordenados e sem órfão. */
  private _passos: PassoResolvido[] = [];

  /** Quantos passos já foram revelados NESTE frame. */
  private _revelados = 0;

  /**
   * Os elementos marcados em frames que a apresentação ainda não alcançou.
   *
   * Card 1864714406189860111 — ver `ocultosDosFuturos` em `passos.ts`. Eles ficam escondidos
   * o tempo todo enquanto se está antes do frame deles, e é isso que impede a abertura
   * panorâmica de entregar o conteúdo dos quadros seguintes.
   */
  private _ocultosFuturos: string[] = [];

  /**
   * Para que lado a apresentação estava indo quando trocou de frame.
   *
   * Quem entra num frame **de trás para frente** o encontra inteiro — foi assim que ele saiu
   * de cena. Entrar com tudo escondido faria `←` mostrar uma tela vazia e obrigar a apertar
   * `→` várias vezes só para voltar ao ponto onde se estava, que é o oposto de voltar.
   */
  private _direcao: 'frente' | 'tras' = 'frente';

  /**
   * O `display` que cada elemento tinha antes de nós o tocarmos.
   *
   * ⚠️ Guardar o valor ANTERIOR, e não assumir `true`, é o que impede a apresentação de
   * "revelar" um elemento que alguém tinha escondido por outro motivo (o mindmap colapsado
   * faz exatamente isso — `mindmap.ts:873`).
   */
  private readonly _displayOriginal = new Map<string, boolean>();

  private get _slots() {
    return this.std.get(EdgelessLegacySlotIdentifier);
  }

  /** A ferramenta de apresentação está ativa AGORA? */
  private _apresentando(): boolean {
    return this.gfx.tool.currentToolOption$.peek()?.toolType === PresentTool;
  }

  /** O frame em apresentação e quantos passos ele tem — para quem desenha o badge. */
  get estado(): {
    frame: FrameBlockModel | null;
    passos: PassoResolvido[];
    revelados: number;
  } {
    return { frame: this._frame, passos: this._passos, revelados: this._revelados };
  }

  override mounted() {
    // 1. O frame mudou — inclusive ao ENTRAR na apresentação (`_moveToCurrentFrame` emite).
    this._disposables.add(
      this._slots.navigatorFrameChanged.subscribe(frame => {
        this._entrarNoFrame(frame);
      })
    );

    // 2. A ferramenta mudou. Sair da apresentação restaura tudo.
    this._disposables.add(
      effect(() => {
        const apresentando =
          this.gfx.tool.currentToolOption$.value?.toolType === PresentTool;
        if (!apresentando) this._restaurarTudo();
      })
    );
  }

  override unmounted() {
    this._restaurarTudo();
    this._disposables.dispose();
  }

  /**
   * Revela o próximo passo. Devolve `true` quando consumiu o avanço — e aí o quadro NÃO muda.
   *
   * É chamada por `_nextFrame()` da barra de apresentação, que é por onde passam a tecla e o
   * botão. Devolver `false` significa *"não tenho mais o que revelar, o avanço é seu"*.
   */
  avancar(): boolean {
    if (!this._apresentando()) return false;
    if (!podeRevelar({ total: totalDePassos(this._passos), revelados: this._revelados })) {
      // Acabaram os passos: o próximo quadro entra pela frente, com tudo escondido.
      this._direcao = 'frente';
      return false;
    }
    this._revelados += 1;
    this._aplicar();
    return true;
  }

  /** Esconde o último passo revelado. Devolve `true` quando consumiu o retrocesso. */
  retroceder(): boolean {
    if (!this._apresentando()) return false;
    if (!podeDesrevelar({ revelados: this._revelados })) {
      // Nada a desfazer: o quadro anterior entra por trás, com tudo revelado.
      this._direcao = 'tras';
      return false;
    }
    this._revelados -= 1;
    this._aplicar();
    return true;
  }

  private _entrarNoFrame(frame: FrameBlockModel) {
    // Sair do frame anterior restaura o que ele escondeu — senão o elemento do quadro 3 fica
    // invisível quando se volta para ele.
    this._restaurarTudo();

    /*
     * ⚠️ **A guarda que faltava, e que o 1º ciclo de campo cobrou.**
     *
     * Este sinal também chega em transições que NÃO são "estou apresentando o quadro X":
     * a barra é renderizada sob demanda e o `updated()` dela dispara `_moveToCurrentFrame`
     * em mais de um caminho. Sem a guarda, um sinal que chegue DEPOIS da saída esconde os
     * elementos e não há mais ninguém para devolvê-los — que é exatamente o print do
     * operador: o quadro em edição, os badges flutuando e **nenhum elemento na tela**.
     *
     * Perguntar ao estado atual em vez de confiar na ordem dos eventos é o que torna isto
     * correto sob qualquer ordenação — e ordenação é justamente o que muda entre uma estação
     * rápida e o aparelho de quem está usando.
     */
    if (!this._apresentando()) return;

    this._frame = frame;
    this._passos = passosDoFrame(frame);
    this._ocultosFuturos = this._calcularOcultosFuturos(frame);
    this._revelados =
      this._direcao === 'tras' ? totalDePassos(this._passos) : 0;
    this._direcao = 'frente';
    this._aplicar();
  }

  /** Escreve o `display` de cada elemento conforme quantos passos já foram revelados. */
  private _aplicar() {
    /*
     * Duas origens, e a segunda é a do card 1864714406189860111: o que falta revelar NESTE
     * frame, mais o que pertence a frames que ainda não chegaram. Sem a segunda, um elemento
     * marcado no `Frame 4` aparece durante o `Start` e some ao entrar no `Frame 4` — a caixa
     * é vista duas vezes e a revelação deixa de revelar coisa alguma.
     */
    const devemSumir = new Set(
      [
        ...ocultosApos(this._passos, this._revelados),
        ...this._ocultosFuturos,
      ].flatMap(id => this.alcance(id).map(m => m.id))
    );

    // Primeiro devolve quem já não precisa sumir — senão um elemento revelado neste passo
    // ficaria escondido até a próxima entrada no quadro.
    for (const [id, original] of [...this._displayOriginal]) {
      if (!devemSumir.has(id)) {
        this._escreverDisplay(id, original);
        this._displayOriginal.delete(id);
      }
    }

    for (const id of devemSumir) {
      if (this._displayOriginal.has(id)) continue;
      const modelo = this.gfx.getElementById(id);
      if (!modelo) continue;
      this._displayOriginal.set(id, lerDisplay(modelo as GfxModel));
      this._escreverDisplay(id, false);
    }

    // Não há refresh manual: mexer num `@local` dispara `elementUpdated`, e o renderer o
    // escuta (`canvas-renderer.ts:1247`). Para bloco, o DOM redesenha sozinho.
  }

  /**
   * Os elementos marcados em frames DEPOIS deste, na ordem da apresentação.
   *
   * A ordem vem do `frames` do `EdgelessFrameManager` — o MESMO getter que a barra de
   * apresentação usa para navegar (`presentation-toolbar.ts:138`). Reordenar por conta
   * própria seria um segundo lugar decidindo qual é o próximo quadro, e os dois divergiriam
   * no dia em que o upstream mexesse no comparador (o `framePresentationComparator` trata o
   * frame legado, sem `presentationIndex`, de um jeito que não se adivinha).
   *
   * ⚠️ Frame que a apresentação não alcança não está aqui: a posição é por ÍNDICE na lista
   * ordenada, e o frame atual é encontrado por identidade. Se ele não estiver na lista (um
   * sinal que chegou fora de hora), o resultado é lista vazia — nada some, que é o lado
   * seguro: esconder demais apaga a apresentação, esconder de menos só mostra algo cedo.
   */
  private _calcularOcultosFuturos(atual: FrameBlockModel): string[] {
    const frames = this.std.get(EdgelessFrameManagerIdentifier).frames;
    const i = frames.indexOf(atual);
    if (i < 0) return [];
    return ocultosDosFuturos(frames.slice(i + 1).map(f => passosDoFrame(f)));
  }

  /** Devolve todo `display` que tocamos ao valor de antes. Idempotente. */
  private _restaurarTudo() {
    if (this._displayOriginal.size > 0) {
      for (const [id, original] of this._displayOriginal) {
        this._escreverDisplay(id, original);
      }
      this._displayOriginal.clear();
    }
    this._frame = null;
    this._passos = [];
    this._ocultosFuturos = [];
    this._revelados = 0;
  }

  /**
   * Esconde ou mostra UM elemento — e são **duas famílias**, com mecanismos diferentes.
   *
   * | Família | Mecanismo | Por quê |
   * |---|---|---|
   * | elemento de canvas | `display = false` (`@local`) | o renderer já o respeita (`canvas-renderer.ts:542` e `:1168`) e a mudança dispara `elementUpdated`, que redesenha sozinho |
   * | bloco (nota, imagem, embed) | **classe** `tuninho-passo-oculto` | `GfxBlockElementModel` **não declara** `hidden`/`display`/`opacity` — não há propriedade de modelo para escrever, e o atributo `style` do host **já tem dono** |
   *
   * ⚠️ **Um frame contém as duas.** Uma implementação testada só com formas passa em tudo e
   * falha na primeira imagem que alguém marcar — é a Armadilha #2 do card.
   *
   * ⚠️ **E a segunda linha da tabela MUDOU no 2º ciclo de campo.** Escrevíamos
   * `host.style.visibility` — a mesma propriedade que `updateBlockVisibility` do upstream
   * reescreve a cada mudança de `transformState$`. Medido no `dev`: um pan e um zoom durante a
   * apresentação faziam a nota **reaparecer sozinha**, com o passo ainda no 0. O porquê e a
   * saída estão em `oculta-bloco.ts`; aqui fica só a chamada.
   */
  private _escreverDisplay(id: string, valor: boolean) {
    const modelo = this.gfx.getElementById(id) as { display?: boolean } | null;
    if (modelo && 'display' in modelo) {
      modelo.display = valor;
      return;
    }

    const host = this.std.view.getBlock(id) as HostDeBloco | null;
    if (host) ocultarBloco(host, !valor, criarNoDeEstilo);
  }

  /**
   * O elemento e tudo o que ele contém.
   *
   * É a correção do que o estudo supôs errado (ver cabeçalho): o renderer não herda `display`,
   * então ocultar um contêiner sem os filhos não oculta nada que se veja.
   */
  alcance(id: string): GfxModel[] {
    const raiz = this.gfx.getElementById(id) as GfxModel | null;
    if (!raiz) return [];
    if (!isGfxGroupCompatibleModel(raiz)) return [raiz];
    return [raiz, ...raiz.descendantElements];
  }
}

/**
 * A única linha deste arquivo que conhece `document` — o núcleo de `oculta-bloco.ts` a recebe
 * injetada para poder ser executado no `node:test` sem navegador.
 */
function criarNoDeEstilo(id: string, css: string): HTMLStyleElement {
  const estilo = document.createElement('style');
  estilo.id = id;
  estilo.textContent = css;
  return estilo;
}

/**
 * O `display` de antes.
 *
 * ⚠️ Guardar o valor ANTERIOR, e não assumir `true`, é o que impede a apresentação de
 * "revelar" um elemento que já estava escondido por outro motivo — o mindmap colapsado faz
 * exatamente isso (`mindmap.ts:873`). Bloco não tem a propriedade, e ausente lê como visível.
 */
function lerDisplay(modelo: GfxModel): boolean {
  const m = modelo as unknown as { display?: boolean };
  return m.display ?? true;
}

/**
 * O controlador deste editor, ou `null`.
 *
 * `getOptional` e não `get`: quem chama roda em contexto onde a extensão pode não estar
 * registrada (o editor de página, por exemplo), e um `throw` ali derrubaria a barra de
 * apresentação inteira por causa de um passo.
 */
export function controladorDePassos(gfx: GfxController): ControladorDePassos | null {
  return (
    (gfx.std.getOptional(
      GfxExtensionIdentifier(CHAVE_DO_CONTROLADOR)
    ) as ControladorDePassos | null) ?? null
  );
}
